/**
 * Cups sold, by size.
 *
 * A cup is any sale line whose size reads like a cup — "Hot 8oz", "Iced 16oz".
 * A size with no ounces in it ("Regular") is not a cup, so a pastry never
 * counts. Keyed on the size name as it was rung, not on the variant id, so a
 * size later renamed in the menu still counts under the name the cup was sold as.
 *
 * A voided sale's cups are left out: a void is the sale that should not have
 * happened. A refund's cups stay in — the drink was made, and the cup was used.
 *
 * Computed live and never frozen. It is a counter for the counter, not one of
 * the figures a Z reading accounts for.
 */

export interface CupLine {
  saleId: string;
  variantNameSnapshot: string | null;
  quantity: number;
}

export interface CupCount {
  size: string;
  count: number;
}

const OUNCES = /(\d+)\s*oz\b/i;

function ounces(size: string): number {
  return Number(OUNCES.exec(size)?.[1] ?? 0);
}

export function countCups(
  lines: ReadonlyArray<CupLine>,
  voidedSaleIds: ReadonlySet<string>,
): { sizes: CupCount[]; total: number } {
  const counts = new Map<string, number>();

  for (const line of lines) {
    const size = line.variantNameSnapshot?.trim();
    if (!size || !OUNCES.test(size)) continue;
    if (voidedSaleIds.has(line.saleId)) continue;
    counts.set(size, (counts.get(size) ?? 0) + line.quantity);
  }

  // Hot before iced, then small to large: the order the cups sit on the shelf.
  const sizes = [...counts.entries()]
    .map(([size, count]) => ({ size, count }))
    .sort((a, b) => {
      const hotA = /^hot/i.test(a.size) ? 0 : 1;
      const hotB = /^hot/i.test(b.size) ? 0 : 1;
      return hotA - hotB || ounces(a.size) - ounces(b.size) || a.size.localeCompare(b.size);
    });

  return { sizes, total: sizes.reduce((sum, s) => sum + s.count, 0) };
}
